// Pure maths for the navigation room's side (elevation) view. No DOM, no Node APIs: unit-tested headlessly
// (scripts/nav-section-test.ts).
//
// The side view is a vertical section through the world. The "section line" runs through the submarine along a
// horizontal axis u (heading, N-S or E-W); the picture's X is the distance s along that line and its Y is depth.
// Everything is in MAP space (see src/shared/spatial.ts): x east, z south (map -z is "north", heading 0).

export type SectionAxis = 'heading' | 'ns' | 'ew';
export const SECTION_AXES: readonly SectionAxis[] = ['heading', 'ns', 'ew'];

export type SectionFrame = {
  originX: number;
  originZ: number;
  ux: number;
  uz: number;
  vx: number;
  vz: number;
};

export const makeSectionFrame = (axis: SectionAxis, originX: number, originZ: number, yawDeg: number): SectionFrame => {
  const yaw = (yawDeg * Math.PI) / 180;
  const ux = axis === 'ns' ? 0 : axis === 'ew' ? 1 : Math.sin(yaw);
  const uz = axis === 'ns' ? -1 : axis === 'ew' ? 0 : -Math.cos(yaw);
  return { originX, originZ, ux, uz, vx: -uz, vz: ux };
};

export const projectToSection = (frame: SectionFrame, x: number, z: number): { s: number; lat: number } => {
  const dx = x - frame.originX;
  const dz = z - frame.originZ;
  return { s: dx * frame.ux + dz * frame.uz, lat: dx * frame.vx + dz * frame.vz };
};

export const unprojectFromSection = (frame: SectionFrame, s: number, lat: number): { x: number; z: number } => ({
  x: frame.originX + frame.ux * s + frame.vx * lat,
  z: frame.originZ + frame.uz * s + frame.vz * lat
});

export const subFacingSign = (frame: SectionFrame, yawDeg: number): 1 | -1 => {
  const yaw = (yawDeg * Math.PI) / 180;
  return Math.sin(yaw) * frame.ux + -Math.cos(yaw) * frame.uz >= 0 ? 1 : -1;
};

export const localYToDepth = (localY: number, layerOffsetY: number, baseDepth: number): number =>
  -(baseDepth + layerOffsetY + localY);

export const heightToDepth = (height: number, baseDepth: number): number => -(baseDepth + height);

export const sampleSeabedProfile = (
  heightAt: (x: number, z: number) => number,
  baseDepth: number,
  frame: SectionFrame,
  sMin: number,
  sMax: number,
  count: number,
  out: Float32Array
): Float32Array => {
  const buffer = out.length >= count ? out : new Float32Array(count);
  const step = count > 1 ? (sMax - sMin) / (count - 1) : 0;
  for (let i = 0; i < count; i += 1) {
    const s = sMin + step * i;
    const h = heightAt(frame.originX + frame.ux * s, frame.originZ + frame.uz * s);
    buffer[i] = Number.isFinite(h) ? heightToDepth(h, baseDepth) : Number.NaN;
  }
  return buffer;
};

export const clipSectionToRect = (
  frame: SectionFrame,
  sMin: number,
  sMax: number,
  minX: number,
  maxX: number,
  minZ: number,
  maxZ: number
): { s0: number; s1: number } | null => {
  let lo = sMin;
  let hi = sMax;
  const axes: Array<[number, number, number, number]> = [
    [frame.originX, frame.ux, minX, maxX],
    [frame.originZ, frame.uz, minZ, maxZ]
  ];
  for (const [o, u, min, max] of axes) {
    if (Math.abs(u) < 1e-9) {
      if (o < min || o > max) return null;
      continue;
    }
    let a = (min - o) / u;
    let b = (max - o) / u;
    if (a > b) { const t = a; a = b; b = t; }
    lo = Math.max(lo, a);
    hi = Math.min(hi, b);
    if (lo > hi) return null;
  }
  return hi > lo ? { s0: lo, s1: hi } : null;
};

export const clipSectionToCircle = (
  frame: SectionFrame,
  sMin: number,
  sMax: number,
  cx: number,
  cz: number,
  radius: number
): { s0: number; s1: number } | null => {
  const dx = frame.originX - cx;
  const dz = frame.originZ - cz;
  const b = dx * frame.ux + dz * frame.uz;
  const c = dx * dx + dz * dz - radius * radius;
  const disc = b * b - c;
  if (disc <= 0) return null;
  const root = Math.sqrt(disc);
  const s0 = Math.max(sMin, -b - root);
  const s1 = Math.min(sMax, -b + root);
  return s1 > s0 ? { s0, s1 } : null;
};

export type TileFootprint = {
  key: string;
  tileY: number;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  originY?: number;
};

export type TileBand = {
  key: string;
  tileY: number;
  s0: number;
  s1: number;
  depthTop: number;
  depthBottom: number;
};

export const tileBandsAlongSection = (
  tiles: readonly TileFootprint[],
  tileSizeY: number,
  baseDepth: number,
  frame: SectionFrame,
  sMin: number,
  sMax: number
): TileBand[] => {
  const bands: TileBand[] = [];
  for (const tile of tiles) {
    const span = clipSectionToRect(frame, sMin, sMax, tile.minX, tile.maxX, tile.minZ, tile.maxZ);
    if (!span) continue;
    const offset = tile.originY ?? tile.tileY * tileSizeY;
    bands.push({
      key: tile.key,
      tileY: tile.tileY,
      s0: span.s0,
      s1: span.s1,
      depthTop: -(baseDepth + offset + tileSizeY / 2),
      depthBottom: -(baseDepth + offset - tileSizeY / 2)
    });
  }
  return bands;
};

export type SectionZone = {
  x: number;
  z: number;
  radius: number;
  halfWidth?: number;
  halfDepth?: number;
  depthMin?: number;
  depthMax?: number;
};

export const zoneSectionSpan = (zone: SectionZone, frame: SectionFrame, sMin: number, sMax: number): { s0: number; s1: number } | null => {
  if (zone.halfWidth !== undefined && zone.halfDepth !== undefined) {
    return clipSectionToRect(frame, sMin, sMax, zone.x - zone.halfWidth, zone.x + zone.halfWidth, zone.z - zone.halfDepth, zone.z + zone.halfDepth);
  }
  return clipSectionToCircle(frame, sMin, sMax, zone.x, zone.z, zone.radius);
};

export const niceTickStep = (pxPerMetre: number, targetPx = 56): number => {
  const raw = targetPx / Math.max(pxPerMetre, 1e-6);
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / pow;
  const nice = norm <= 1.5 ? 1 : norm <= 3.5 ? 2 : norm <= 7.5 ? 5 : 10;
  return nice * pow;
};

export const stubPolyline = (points: ReadonlyArray<readonly [number, number, number]>, fraction: number, fromStart: boolean): Array<[number, number, number]> => {
  const ordered = fromStart ? points.slice() : points.slice().reverse();
  if (ordered.length < 2) return ordered.map((p) => [p[0], p[1], p[2]]);
  let total = 0;
  const lens: number[] = [];
  for (let i = 1; i < ordered.length; i += 1) {
    const l = Math.hypot(ordered[i][0] - ordered[i - 1][0], ordered[i][1] - ordered[i - 1][1], ordered[i][2] - ordered[i - 1][2]);
    lens.push(l);
    total += l;
  }
  const target = total * fraction;
  const out: Array<[number, number, number]> = [[ordered[0][0], ordered[0][1], ordered[0][2]]];
  let walked = 0;
  for (let i = 0; i < lens.length; i += 1) {
    const a = ordered[i];
    const b = ordered[i + 1];
    if (walked + lens[i] >= target) {
      const t = lens[i] > 0 ? (target - walked) / lens[i] : 0;
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]);
      return out;
    }
    out.push([b[0], b[1], b[2]]);
    walked += lens[i];
  }
  return out;
};

export const ENCLOSED_TILE_KINDS: ReadonlySet<string> = new Set(['cave_maze', 'narrow_tunnel', 'grand_cavern', 'hive_cavern', 'vertical_shaft', 'sewer', 'drill_cavern', 'drill_shaft', 'drill_base']);

export const isEnclosedTileKind = (kind: string | null | undefined): boolean => !!kind && ENCLOSED_TILE_KINDS.has(kind);

export const enclosedTileLayerKeys = (
  tiles: readonly TileFootprint[],
  nodes: ReadonlyArray<{ x: number; z: number; depth: number; enclosed: boolean }>,
  tileSizeY: number,
  baseDepth: number
): Set<string> => {
  const keys = new Set<string>();
  for (const node of nodes) {
    if (!node.enclosed) continue;
    for (const tile of tiles) {
      if (node.x < tile.minX || node.x > tile.maxX || node.z < tile.minZ || node.z > tile.maxZ) continue;
      const offset = tile.originY ?? tile.tileY * tileSizeY;
      const top = -(baseDepth + offset + tileSizeY / 2);
      const bottom = -(baseDepth + offset - tileSizeY / 2);
      if (node.depth >= top && node.depth <= bottom) keys.add(tile.key);
    }
  }
  return keys;
};

export type NavGatewayEntry = {
  key: string;
  side: string;
  x: number;
  y: number;
  z: number;
  radius: number;
  nodeKeys: string[];
};
